import {
  AfterViewInit,
  Directive,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  signal,
} from '@angular/core';

@Directive({
  selector: '[workUiAutoHeightTextarea]',
  standalone: true,
})
export class AutoHeightTextareaDirective implements AfterViewInit, OnDestroy {
  private static readonly DEFAULT_ROWS = 2;
  private static readonly DEFAULT_FALLBACK_HEIGHT = 104;
  private static readonly DEFAULT_LINE_HEIGHT = 20;
  private static readonly BORDER_HEIGHT = 2;

  private elementRef = inject(ElementRef);
  private resizeObserver: ResizeObserver | null = null;
  private initialHeight = 0;
  private initializationRetries = 0;

  /**
   * Controls whether the auto-height behavior is enabled for this textarea.
   * When true, the textarea will automatically adjust its height based on content.
   * @default false
   */
  public readonly workUiAutoHeightTextarea = input(false);

  /**
   * The current value of the textarea.
   * This input is tracked to trigger height recalculation when the content changes programmatically.
   */
  public readonly workUiAutoHeightTextareaValue = input.required<string | null | undefined>();

  private previousActive = signal(false);

  // Effect to handle activation state changes
  private workUiAutoHeightTextareaEffect = effect(() => {
    const active = this.workUiAutoHeightTextarea();
    const previousActive = this.previousActive();

    this.previousActive.set(active);

    // If transitioning from inactive to active after view init, recalculate
    if (!previousActive && active && this.elementRef.nativeElement) {
      requestAnimationFrame(() => {
        this.initializeHeightCalculation();
      });
    }
  });

  // Effect to handle value changes
  private workUiAutoHeightTextareaValueEffect = effect(() => {
    // Track value changes and enabled state
    this.workUiAutoHeightTextareaValue();

    if (this.workUiAutoHeightTextarea() && this.elementRef.nativeElement) {
      requestAnimationFrame(() => {
        this.adjustHeight();
      });
    }
  });

  ngAfterViewInit() {
    if (this.workUiAutoHeightTextarea()) {
      // Defer initialization to ensure styles are fully applied
      requestAnimationFrame(() => {
        this.initializeHeightCalculation();
      });
    }
  }

  ngOnDestroy() {
    this.disconnectResizeObserver();
  }

  @HostListener('input')
  onInput(): void {
    this.adjustHeight();
  }

  private initializeHeightCalculation(): void {
    this.initialHeight = this.getInitialHeightFromRows();

    // If styles aren't ready yet, retry after another frame
    if (this.initialHeight === 0) {
      this.initializationRetries++;
      requestAnimationFrame(() => {
        this.initializeHeightCalculation();
      });
      return;
    }

    this.setupResizeObserver();
    // Perform initial adjustment for pre-filled content
    this.adjustHeight();
  }

  private adjustHeight(): void {
    if (!this.workUiAutoHeightTextarea()) {
      return;
    }

    const textArea = this.elementRef?.nativeElement;
    if (!textArea) {
      return;
    }

    // Reset the height temporarily to get the correct scrollHeight
    textArea.style.height = 'auto';

    // Force a reflow by reading offsetHeight (or any layout property)
    void textArea.offsetHeight;

    const contentHeight = (textArea.scrollHeight > 0 ? textArea.scrollHeight : AutoHeightTextareaDirective.DEFAULT_FALLBACK_HEIGHT) + AutoHeightTextareaDirective.BORDER_HEIGHT;
    const newHeight = Math.max(this.initialHeight, contentHeight);
    textArea.style.height = newHeight + 'px';
  }

  private getInitialHeightFromRows(): number {
    const textArea = this.elementRef.nativeElement as HTMLTextAreaElement;

    // Get the rows attribute, default to 2 if not specified
    const rows = textArea.rows || AutoHeightTextareaDirective.DEFAULT_ROWS;

    // Try to get computed line height
    const computedStyle = window.getComputedStyle(textArea);
    const parsedLineHeight = parseInt(computedStyle.lineHeight, 10);
    const lineHeight = (!isNaN(parsedLineHeight) && parsedLineHeight > 0) ? parsedLineHeight : AutoHeightTextareaDirective.DEFAULT_LINE_HEIGHT;

    // Add padding and border to the calculation
    const paddingTop = parseInt(computedStyle.paddingTop, 10) || 0;
    const paddingBottom = parseInt(computedStyle.paddingBottom, 10) || 0;
    const borderTop = parseInt(computedStyle.borderTopWidth, 10) || 0;
    const borderBottom = parseInt(computedStyle.borderBottomWidth, 10) || 0;

    // Calculate height based on rows, line height, padding and border
    return rows * lineHeight + paddingTop + paddingBottom + borderTop + borderBottom;
  }

  private setupResizeObserver(): void {
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.adjustHeight();
      });
      this.resizeObserver.observe(this.elementRef.nativeElement);
    }
  }

  private disconnectResizeObserver(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }
}
